import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import api, { formatDate } from "@/lib/api";
import SEO from "@/components/SEO";
import { FadeUp } from "@/components/Motion";

export default function Article() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);


  useEffect(() => {
    setPost(null);
    setNotFound(false);

    api
      .get(`/journal/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div
        className="flex min-h-[70vh] flex-col items-center justify-center gap-6 pt-16"
        data-testid="article-not-found"
      >
        <h1 className="font-display text-3xl font-bold">Article not found</h1>

        <Link
          to="/journal"
          className="rounded-full bg-ink px-6 py-3 text-sm font-semibold text-cream dark:bg-cream dark:text-ink"
        >
          Back to journal
        </Link>
      </div>
    );
  }

  if (!post) {
    return (
      <div
        className="flex min-h-[70vh] items-center justify-center pt-16 text-muted-foreground"
        data-testid="article-loading"
      >
        Loading…
      </div>
    );
  }

  const seoDescription = (
    post.excerpt ||
    post.content?.[0] ||
    "Read the latest production stories, case studies and filmmaking insights from Incolour Films."
  ).slice(0, 160);

  return (
    <article
      className="pt-16"
      data-testid={`article-page-${post.slug}`}
    >
      <SEO
        title={post.title}
        description={seoDescription}
        image={post.image}
        path={`/journal/${post.slug}`}
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: post.title,
          description: seoDescription,
          image: post.image,
          datePublished: post.published_at,
          dateModified: post.updated_at || post.published_at,
          author: {
            "@type": "Person",
            name: post.author || "Incolour Films",
          },
          publisher: {
            "@type": "Organization",
            name: "Incolour Films",
            url: "https://incolourfilms.ca",
            logo: {
              "@type": "ImageObject",
              url: "https://incolourfilms.ca/logo-mark.webp",
            },
          },
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id": `https://incolourfilms.ca/journal/${post.slug}`,
          },
        }}
      />

      <header className="mx-auto max-w-3xl px-4 pt-16 sm:px-6 lg:pt-24">
        <Link
          to="/journal"
          data-testid="article-back-link"
          className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground transition-colors hover:text-orange"
        >
          <ArrowLeft size={14} /> Journal
        </Link>

        <p className="mt-8 text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">
          <span className="text-orange">{post.category}</span> —{" "}
          {formatDate(post.published_at)} — By {post.author}
        </p>

        <h1
          className="mt-5 font-display text-3xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl"
          data-testid="article-title"
        >
          {post.title}
        </h1>

        {post.excerpt && (
          <p className="mt-6 font-serif text-xl italic leading-relaxed text-muted-foreground">
            {post.excerpt}
          </p>
        )}
      </header>

      {post.image && (
        <FadeUp className="mx-auto mt-12 max-w-5xl px-4 sm:px-6">
          <div className="overflow-hidden rounded-2xl border border-border">
            <img
              src={post.image}
              alt={post.title}
              className="aspect-[16/8] w-full object-cover"
            />
          </div>
        </FadeUp>
      )}

      <div
        className="mx-auto max-w-3xl px-4 py-16 sm:px-6"
        data-testid="article-body"
      >
        {post.content.map((para, i) => (
          <FadeUp
            key={i}
            delay={Math.min(i * 0.05, 0.2)}
          >
            <p
              className={`text-base leading-loose sm:text-lg ${
                i > 0 ? "mt-8" : ""
              } ${
                i === 0
                  ? "first-letter:float-left first-letter:mr-3 first-letter:font-display first-letter:text-6xl first-letter:font-extrabold first-letter:leading-[0.85] first-letter:text-orange"
                  : ""
              }`}
            >
              {para}
            </p>
          </FadeUp>
        ))}

        <div className="mt-16 border-t border-border pt-8">
          <Link
            to="/journal"
            className="text-sm font-semibold text-orange"
            data-testid="article-more-link"
          >
            ← More from the journal
          </Link>
        </div>
      </div>
    </article>
  );
}
